import React, { useEffect, useRef, useState } from "react";
import { PageHeader, Card, Tag } from "../components/UI";
import { Send, Phone, MoreVertical, Radio, CheckCircle2, Loader2, RefreshCw, AlertTriangle, Inbox } from "lucide-react";
import api, { formatError } from "../lib/api";

export default function WhatsAppStudio() {
  const [mobile, setMobile] = useState("");
  const [physios, setPhysios] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("Hi");
  const [status, setStatus] = useState(null);
  const [liveOpen, setLiveOpen] = useState(false);
  const [liveTo, setLiveTo] = useState("");
  const [liveText, setLiveText] = useState("Hello from CBC Loyalty — testing the Karix WhatsApp integration. Reply MENU for options.");
  const [liveBusy, setLiveBusy] = useState(false);
  const [liveResult, setLiveResult] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => { (async () => {
    const [a, b] = await Promise.all([api.get("/physios?limit=30"), api.get("/integrations/status")]);
    setPhysios(a.data); setStatus(b.data);
    if (a.data[0]) setMobile(a.data[0].mobile);
    loadDiag();
  })(); }, []);

  async function loadDiag() {
    setDiagBusy(true);
    try { const { data } = await api.get("/whatsapp/debug?limit=15"); setDiag(data); }
    catch (e) { setDiag({ error: formatError(e) }); }
    setDiagBusy(false);
  }

  useEffect(() => { if (mobile) loadHistory(); }, [mobile]);

  async function loadHistory() {
    const { data } = await api.get(`/whatsapp/history/${mobile}`);
    setMessages(data);
    setTimeout(() => ref.current?.scrollTo({ top: 9e9 }), 50);
  }

  async function send() {
    if (!text.trim() || !mobile) return;
    await api.post("/whatsapp/inbound", { mobile, text });
    setText("");
    loadHistory();
  }

  async function sendLive() {
    setLiveBusy(true); setLiveResult(null);
    try {
      const { data } = await api.post("/whatsapp/send", { to: liveTo, text: liveText });
      setLiveResult(data);
    } catch (e) { setLiveResult({ ok: false, error: formatError(e) }); }
    setLiveBusy(false);
  }

  const physio = physios.find((p) => p.mobile === mobile);
  const wa = status?.whatsapp;

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Engagement"
        title="WhatsApp Studio"
        subtitle="The CBC platform is WhatsApp-first. The Karix RCM WhatsApp Business API is live; production sends route through the integration. Use the simulator on the right to pilot conversational flows."
        testId="whatsapp-page"
        actions={wa?.configured ?
          <button onClick={() => setLiveOpen(true)} className="btn-teal flex items-center gap-2" data-testid="send-live-wa-btn">
            <Radio size={14} /> Send live test
          </button> : null}
      />

      {/* Provider status strip */}
      <Card title="WhatsApp routing" subtitle="Configure these webhook URLs inside the Karix dashboard — one for preview testing, one for production.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[12px]">
          <div className="surface p-3">
            <div className="small-caps text-slate-400">Provider</div>
            <div className="font-medium mt-1 flex items-center gap-2">{wa?.provider || "—"} {wa?.configured && <CheckCircle2 size={14} className="text-emerald-600" />}</div>
            <div className="small-caps text-slate-400 mt-3">Sending number</div>
            <div className="font-mono mt-1">{wa?.from_number || "—"}</div>
            <div className="small-caps text-slate-400 mt-3">Status</div>
            <div className="mt-1">{wa?.configured ? <Tag tone="emerald">Connected</Tag> : <Tag tone="amber">Not configured</Tag>}</div>
          </div>
          <div className="surface p-3 space-y-3">
            <div>
              <div className="small-caps text-slate-400 flex items-center gap-1.5">Preview webhook <Tag tone="amber">staging</Tag></div>
              <div className="font-mono text-[11px] mt-1 break-all bg-slate-50 border border-slate-100 rounded px-2 py-1.5" data-testid="wa-webhook-preview">
                {status?.whatsapp?.webhook_preview || "—"}
              </div>
              {status?.whatsapp?.webhook_preview && <button onClick={() => navigator.clipboard.writeText(status.whatsapp.webhook_preview)} className="text-[11px] text-teal-700 hover:underline mt-1">Copy</button>}
            </div>
            <div>
              <div className="small-caps text-slate-400 flex items-center gap-1.5">Production inbound (MO) webhook <Tag tone="emerald">live</Tag></div>
              <div className="font-mono text-[11px] mt-1 break-all bg-slate-50 border border-slate-100 rounded px-2 py-1.5" data-testid="wa-webhook-production">
                {status?.whatsapp?.webhook_production || "—"}
              </div>
              {status?.whatsapp?.webhook_production && <button onClick={() => navigator.clipboard.writeText(status.whatsapp.webhook_production)} className="text-[11px] text-teal-700 hover:underline mt-1">Copy</button>}
            </div>
            <div>
              <div className="small-caps text-slate-400 flex items-center gap-1.5">Production delivery (DN) webhook <Tag tone="emerald">live</Tag></div>
              <div className="font-mono text-[11px] mt-1 break-all bg-slate-50 border border-slate-100 rounded px-2 py-1.5" data-testid="wa-delivery-production">
                {status?.whatsapp?.delivery_production || "—"}
              </div>
              {status?.whatsapp?.delivery_production && <button onClick={() => navigator.clipboard.writeText(status.whatsapp.delivery_production)} className="text-[11px] text-teal-700 hover:underline mt-1">Copy</button>}
            </div>
          </div>
        </div>
        <div className="surface bg-amber-50 border border-amber-200 p-3 flex gap-2 text-[12px] text-amber-900 mt-3" data-testid="wa-webhook-reminder">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div>
            <b>Auto-replies not working on WhatsApp?</b> The #1 cause is the Karix dashboard still pointing at the <b>preview</b> URL. Log in to Karix → set the <b>inbound (MO)</b> webhook to the <b>Production inbound</b> URL above, and the <b>delivery (DN)</b> webhook to the <b>Production delivery</b> URL (with <code className="font-mono">webHookDNId</code> = <span className="font-mono">1001</span>). Then message the number and check the diagnostics below.
          </div>
        </div>
      </Card>

      {/* Webhook diagnostics — what Karix actually posted + whether replies were accepted */}
      <Card
        title="Webhook diagnostics"
        subtitle="Debug 'no response on WhatsApp'. After a physio messages your number, refresh to see what Karix posted and whether our auto-reply was accepted."
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-[12px] text-slate-500">Live view of the last inbound messages, auto-replies, delivery callbacks and any payloads we couldn't parse.</div>
          <button onClick={loadDiag} disabled={diagBusy} className="btn-ghost border border-slate-200 rounded-lg !py-1.5 !px-3 flex items-center gap-1.5 text-[12px]" data-testid="wa-diag-refresh">
            {diagBusy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh
          </button>
        </div>

        {diag?.error && <div className="tag tag-rose w-full justify-start" data-testid="wa-diag-error">{diag.error}</div>}

        {diag && !diag.error && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3" data-testid="wa-diag-counts">
              {[["Inbound", diag.counts?.inbound, "border-teal-500"], ["Auto-replies", diag.counts?.outbound, "border-emerald-500"], ["Send failures", diag.counts?.outbound_send_failures, "border-rose-500"], ["Delivery cbk", diag.counts?.delivery_callbacks, "border-slate-300"], ["Unparsed", diag.counts?.unparsed_webhooks, "border-amber-500"]].map(([k, v, cls]) => (
                <div key={k} className={`surface p-3 text-center border-l-2 ${cls}`}>
                  <div className="serif-display text-2xl numerals">{v ?? 0}</div>
                  <div className="small-caps text-slate-400 mt-0.5">{k}</div>
                </div>
              ))}
            </div>

            {(diag.counts?.inbound === 0 && diag.counts?.unparsed_webhooks === 0) && (
              <div className="surface bg-amber-50 border border-amber-200 p-3 flex gap-2 text-[12px] text-amber-800 mb-3" data-testid="wa-diag-hint">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <div>No inbound messages and no unparsed webhooks have arrived. This means <b>Karix is not reaching this webhook</b>. Verify the inbound webhook URL in your Karix dashboard matches the Production URL above, and that the WhatsApp number is live on Karix.</div>
              </div>
            )}
            {diag.counts?.outbound_send_failures > 0 && (
              <div className="surface bg-rose-50 border border-rose-200 p-3 flex gap-2 text-[12px] text-rose-800 mb-3" data-testid="wa-diag-sendfail">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <div>Inbound is arriving but our <b>auto-reply was rejected by Karix</b> ({diag.counts.outbound_send_failures} fail{diag.counts.outbound_send_failures > 1 ? "s" : ""}). Expand an outbound row below to see the exact Karix error (often: wrong sender number, or a template is required outside the 24h window).</div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="small-caps text-slate-400 mb-1 flex items-center gap-1.5"><Inbox size={12} /> Recent inbound</div>
                <div className="space-y-1 max-h-56 overflow-auto">
                  {(diag.inbound || []).length === 0 ? <div className="text-[12px] text-slate-400 py-3">No inbound messages yet.</div> :
                    diag.inbound.map((m) => (
                      <div key={m.id} className="surface p-2 text-[12px]" data-testid="wa-diag-inbound-row">
                        <div className="flex justify-between"><span className="font-mono text-slate-700">{m.mobile}</span><span className="text-[10px] text-slate-400">{(m.at || "").slice(0, 19).replace("T", " ")}</span></div>
                        <div className="text-slate-600 mt-0.5">{m.text || "—"}</div>
                      </div>
                    ))}
                </div>
              </div>
              <div>
                <div className="small-caps text-slate-400 mb-1 flex items-center gap-1.5"><Send size={12} /> Recent auto-replies</div>
                <div className="space-y-1 max-h-56 overflow-auto">
                  {(diag.outbound || []).length === 0 ? <div className="text-[12px] text-slate-400 py-3">No replies sent yet.</div> :
                    diag.outbound.map((m) => {
                      const ok = (m.delivery || {}).ok; const skipped = (m.delivery || {}).skipped;
                      return (
                        <details key={m.id} className="surface p-2 text-[12px]" data-testid="wa-diag-outbound-row">
                          <summary className="flex justify-between cursor-pointer list-none">
                            <span className="font-mono text-slate-700">{m.mobile}</span>
                            {skipped ? <Tag tone="slate">simulated</Tag> : ok ? <Tag tone="emerald">accepted</Tag> : <Tag tone="rose">rejected</Tag>}
                          </summary>
                          <div className="text-slate-600 mt-1 whitespace-pre-line">{(m.text || "").slice(0, 160)}</div>
                          {!skipped && <pre className="text-[10px] bg-slate-50 border border-slate-100 rounded p-1.5 mt-1 overflow-auto max-h-32">{JSON.stringify(m.delivery, null, 2)}</pre>}
                        </details>
                      );
                    })}
                </div>
              </div>
            </div>

            {(diag.unparsed || []).length > 0 && (
              <div className="mt-3">
                <div className="small-caps text-slate-400 mb-1 flex items-center gap-1.5"><AlertTriangle size={12} /> Unparsed webhook payloads (raw)</div>
                <div className="space-y-1 max-h-56 overflow-auto">
                  {diag.unparsed.map((e) => (
                    <details key={e.id} className="surface p-2 text-[12px]" data-testid="wa-diag-unparsed-row">
                      <summary className="cursor-pointer text-slate-500">{(e.at || "").slice(0, 19).replace("T", " ")} · {e.content_type || "?"}</summary>
                      <pre className="text-[10px] bg-slate-50 border border-slate-100 rounded p-1.5 mt-1 overflow-auto max-h-40">{JSON.stringify(e.data, null, 2)}</pre>
                    </details>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Card>


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Test on behalf of a physio" subtitle="Simulate inbound from a seeded physio number to preview conversational replies." className="lg:col-span-1">
          <div className="space-y-1 max-h-[520px] overflow-y-auto">
            {physios.map((p) => (
              <button key={p.id} onClick={() => setMobile(p.mobile)} className={`w-full text-left px-3 py-2 rounded-md border text-[13px] ${mobile === p.mobile ? "border-teal-700 bg-teal-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`wa-physio-${p.id}`}>
                <div className="font-medium">{p.first_name} {p.last_name}</div>
                <div className="text-[11px] text-slate-500 font-mono">{p.mobile}</div>
              </button>
            ))}
          </div>
        </Card>

        <div className="lg:col-span-2 flex justify-center">
          {/* Phone frame */}
          <div className="w-[340px] h-[640px] border-[10px] border-slate-900 rounded-[2.5rem] bg-slate-50 shadow-xl overflow-hidden flex flex-col relative" data-testid="wa-phone-frame">
            <div className="bg-teal-700 text-white p-3 flex items-center gap-2 text-sm">
              <Phone size={16} />
              <div className="leading-tight flex-1">
                <div className="font-medium">CBC · Jenburkt</div>
                <div className="text-[10px] opacity-80">{physio ? `${physio.first_name} ${physio.last_name}` : "—"}</div>
              </div>
              <MoreVertical size={16} />
            </div>
            <div ref={ref} className="flex-1 overflow-y-auto p-3 flex flex-col gap-2" style={{ background: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2260%22 height=%2260%22><circle cx=%221%22 cy=%221%22 r=%220.5%22 fill=%22%23cbd5e1%22/></svg>')" }}>
              {messages.length === 0 && <div className="text-center text-[11px] text-slate-400 mt-6">Send &quot;Hi&quot; to start a CBC session.</div>}
              {messages.map((m) => (
                <div key={m.id} className={`${m.direction === "in" ? "self-end bg-teal-50 border-teal-100 rounded-tr-sm" : "self-start bg-white border-slate-200 rounded-tl-sm"} border rounded-2xl p-2.5 text-[12.5px] max-w-[80%] whitespace-pre-line shadow-sm`}>
                  {m.text}
                </div>
              ))}
            </div>
            <div className="bg-white border-t border-slate-200 p-2 flex items-center gap-1.5">
              <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="flex-1 !py-2 !text-[12px]" data-testid="wa-input" />
              <button onClick={send} className="bg-teal-700 text-white p-2 rounded-full hover:bg-teal-600" data-testid="wa-send"><Send size={14} /></button>
            </div>
          </div>
        </div>
      </div>

      {liveOpen && (
        <div className="fixed inset-0 bg-slate-900/30 z-40 flex items-center justify-center p-4" onClick={() => setLiveOpen(false)}>
          <div className="bg-white rounded-xl border border-slate-200 w-full max-w-md p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="live-wa-modal">
            <h3 className="serif-display text-2xl">Send live WhatsApp</h3>
            <p className="text-[12px] text-slate-500">Routed through Karix RCM from <span className="font-mono">{wa?.from_number}</span>. Note: business-initiated messages outside a 24h window need an approved template.</p>
            <div><label className="small-caps text-slate-500">Recipient (E.164 or 10-digit)</label><input value={liveTo} onChange={(e) => setLiveTo(e.target.value)} placeholder="91XXXXXXXXXX" /></div>
            <div><label className="small-caps text-slate-500">Message</label><textarea value={liveText} onChange={(e) => setLiveText(e.target.value)} rows={4} className="w-full" /></div>
            <button onClick={sendLive} disabled={liveBusy || !liveTo} className="btn-teal w-full flex items-center justify-center gap-2">
              {liveBusy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Radio size={14} /> Send via Karix</>}
            </button>
            {liveResult && (
              <pre className="text-[11px] bg-slate-50 border border-slate-100 rounded p-2 mono max-h-40 overflow-auto">{JSON.stringify(liveResult, null, 2)}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
